import { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Row, Col, Form, Button, InputGroup } from 'react-bootstrap';
import { FiArrowLeft, FiSave, FiFileText } from 'react-icons/fi';
import Card from '../Components/Card';
import FormField from '../Components/FormField';
import StationPicker from '../Components/StationPicker';
import ConfirmDialog from '../Components/ConfirmDialog';
import { useMasterData } from '../Context/masterDataContext';
import { useCreateRetailer, useUpdateRetailer, useRetailer, useRetailerStations } from '../hooks/useRetailers';
import { useSaveDraft } from '../hooks/useDrafts';
import { getDraft } from '../Api/draftsApi';

const PO = 'SFMACUCM';
const EMPTY = {
  name: '', mobileCountryCode: '91', mobileNumber: '', gstno: '', pan: '',
  stationGuid: null, pincode: '',
  geo: {
    countryCode: '', regionName: '', districtName: '', tehsilName: '', cityName: '', cityCode: '',
    countryGuid: null, regionGuid: null, districtGuid: null, tehsilGuid: null, cityGuid: null, pincodeGuid: null,
  },
};

export default function RetailerFormPage() {
  const { cmGuid } = useParams();
  const isEdit = Boolean(cmGuid);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const draftFromUrl = params.get('draft');

  const create = useCreateRetailer();
  const update = useUpdateRetailer(cmGuid);
  const saveDraftM = useSaveDraft();
  const { data: detail } = useRetailer(cmGuid);
  const { data: stations } = useRetailerStations(cmGuid);
  const { useGeoByPincode } = useMasterData();

  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [draftGuid, setDraftGuid] = useState(draftFromUrl || null);
  const [lookupPincode, setLookupPincode] = useState('');
  const [gstPrompt, setGstPrompt] = useState(false);

  const geoQ = useGeoByPincode(lookupPincode);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const setGeo = (patch) => setForm((f) => ({ ...f, geo: { ...f.geo, ...patch } }));

  // Pincode -> autofill geo
  useEffect(() => {
    if (geoQ.data) {
      setForm((f) => ({
        ...f,
        geo: {
          countryCode: geoQ.data.countryCode || '', regionName: geoQ.data.regionName || '',
          districtName: geoQ.data.districtName || '', tehsilName: geoQ.data.tehsilName || '',
          cityName: geoQ.data.cityName || '', cityCode: geoQ.data.cityCode || '',
          countryGuid: geoQ.data.countryGuid || null, regionGuid: geoQ.data.regionGuid || null,
          districtGuid: geoQ.data.districtGuid || null, tehsilGuid: geoQ.data.tehsilGuid || null,
          cityGuid: geoQ.data.cityGuid || null, pincodeGuid: geoQ.data.pincodeGuid || null,
        },
      }));
    }
  }, [geoQ.data]);

  // Edit prefill
  useEffect(() => {
    if (!isEdit || !detail) return;
    setForm((f) => ({
      ...f, name: detail.customerName || '', mobileCountryCode: detail.mobileCountryCode || '91',
      mobileNumber: detail.mobileNumber || '', gstno: detail.gstin || '', pan: detail.pan || '',
      pincode: detail.pincode || '',
      geo: {
        countryCode: detail.countryCode || '', regionName: detail.regionName || '', districtName: detail.districtName || '',
        tehsilName: detail.tehsilName || '', cityName: detail.cityName || '', cityCode: detail.cityCode || '',
        countryGuid: detail.countryGuid || null, regionGuid: detail.regionGuid || null,
        districtGuid: detail.districtGuid || null, tehsilGuid: detail.tehsilGuid || null,
        cityGuid: detail.cityGuid || null, pincodeGuid: detail.pincodeGuid || null,
      },
    }));
  }, [isEdit, detail]);

  useEffect(() => {
    if (isEdit && stations?.length) setForm((f) => ({ ...f, stationGuid: f.stationGuid || stations[0].stationGuid }));
  }, [isEdit, stations]);

  // Draft resume
  useEffect(() => {
    if (!draftFromUrl) return;
    getDraft(draftFromUrl).then((d) => {
      if (d?.payloadJson) { try { setForm({ ...EMPTY, ...JSON.parse(d.payloadJson) }); } catch { /* ignore */ } }
    });
  }, [draftFromUrl]);

  const validateFinal = () => {
    const e = {};
    if (!form.stationGuid) e.stationGuid = 'Select a station first.';
    if (!form.name.trim()) e.name = 'Retailer name is required.';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const buildBase = () => ({
    name: form.name.trim(),
    countryCode: form.geo.countryCode || null, regionName: form.geo.regionName || null,
    districtName: form.geo.districtName || null, tehsilName: form.geo.tehsilName || null,
    cityName: form.geo.cityName || null, cityCode: form.geo.cityCode || null, pincode: form.pincode || null,
    countryGuid: form.geo.countryGuid || null, regionGuid: form.geo.regionGuid || null,
    districtGuid: form.geo.districtGuid || null, tehsilGuid: form.geo.tehsilGuid || null,
    cityGuid: form.geo.cityGuid || null, pincodeGuid: form.geo.pincodeGuid || null,
    gstno: form.gstno || null, pan: form.pan || null,
    mobileCountryCode: form.mobileCountryCode || null, mobileNumber: form.mobileNumber || null,
    stationGuid: form.stationGuid,
  });

  const commit = () => {
    if (isEdit) {
      update.mutate(buildBase(), { onSuccess: () => navigate(`/retailers/${cmGuid}`) });
    } else {
      create.mutate({ ...buildBase(), draftGuid: draftGuid || null }, { onSuccess: () => navigate('/retailers') });
    }
  };

  const onSave = (ev) => {
    ev.preventDefault();
    if (!validateFinal()) return;
    if (!form.gstno?.trim()) { setGstPrompt(true); return; }  // prompt if GSTIN blank
    commit();
  };

  const onSaveDraft = () => {
    if (!form.name.trim()) { setErrors({ name: 'Enter a name to save a draft.' }); return; }
    saveDraftM.mutate(
      { poCode: PO, title: form.name.trim(), payloadJson: JSON.stringify(form), draftGuid: draftGuid || null },
      { onSuccess: () => navigate('/retailers/drafts') }
    );
  };

  const busy = create.isPending || update.isPending || saveDraftM.isPending;

  return (
    <div className="hrms-page">
      <div className="hrms-toolbar">
        <h1 className="hrms-page-title">{isEdit ? 'Edit Retailer' : 'New Retailer'}</h1>
        <Button variant="light" onClick={() => navigate(isEdit ? `/retailers/${cmGuid}` : '/retailers')}><FiArrowLeft className="me-1" /> Back</Button>
      </div>

      <Form onSubmit={onSave}>
        {/* Step 1 — Station (only the user's tagged stations) */}
        <Card title="1. Station (ownership)" className="mb-3">
          <Row><Col md={6}>
            <FormField label="Station" required error={errors.stationGuid} hint="You can only see your assigned stations.">
              <StationPicker value={form.stationGuid} onChange={(stationGuid) => set({ stationGuid })} />
            </FormField>
          </Col></Row>
        </Card>

        {/* Step 2 — Pincode -> location autofill */}
        <Card title="2. Location (from pincode)" className="mb-3">
          <Row>
            <Col md={3}>
              <FormField label="Pincode" hint="Enter to auto-fill location">
                <Form.Control value={form.pincode}
                  onChange={(e) => set({ pincode: e.target.value })}
                  onBlur={(e) => setLookupPincode(e.target.value.trim())} placeholder="e.g. 110015" />
              </FormField>
            </Col>
            <Col md={3}><FormField label="City"><Form.Control value={form.geo.cityName} onChange={(e) => setGeo({ cityName: e.target.value })} /></FormField></Col>
            <Col md={3}><FormField label="Tehsil"><Form.Control value={form.geo.tehsilName} onChange={(e) => setGeo({ tehsilName: e.target.value })} /></FormField></Col>
            <Col md={3}><FormField label="District"><Form.Control value={form.geo.districtName} onChange={(e) => setGeo({ districtName: e.target.value })} /></FormField></Col>
            <Col md={3}><FormField label="State"><Form.Control value={form.geo.regionName} onChange={(e) => setGeo({ regionName: e.target.value })} /></FormField></Col>
            <Col md={3}><FormField label="Country"><Form.Control value={form.geo.countryCode} onChange={(e) => setGeo({ countryCode: e.target.value })} /></FormField></Col>
          </Row>
        </Card>

        {/* Step 3 — Name (+ details). Code auto-generated on save. */}
        <Card title="3. Retailer details" className="mb-3">
          <Row>
            <Col md={8}>
              <FormField label="Retailer name" required error={errors.name} hint={isEdit ? null : 'Code auto-generates as RET###### on save.'}>
                <Form.Control value={form.name} onChange={(e) => set({ name: e.target.value })} maxLength={510} />
              </FormField>
            </Col>
            {isEdit && <Col md={4}><FormField label="Code"><Form.Control value={detail?.customerCode || ''} disabled /></FormField></Col>}
            <Col md={5}>
              <FormField label="Mobile">
                <InputGroup>
                  <Form.Control value={form.mobileCountryCode} onChange={(e) => set({ mobileCountryCode: e.target.value })} maxLength={2} style={{ maxWidth: 56 }} aria-label="Country code" />
                  <Form.Control value={form.mobileNumber} onChange={(e) => set({ mobileNumber: e.target.value })} maxLength={20} placeholder="Mobile number" aria-label="Mobile number" />
                </InputGroup>
              </FormField>
            </Col>
            <Col md={3}><FormField label="GSTIN" hint="Optional"><Form.Control value={form.gstno} onChange={(e) => set({ gstno: e.target.value })} maxLength={30} /></FormField></Col>
            <Col md={3}><FormField label="PAN" hint="Optional"><Form.Control value={form.pan} onChange={(e) => set({ pan: e.target.value })} maxLength={20} /></FormField></Col>
          </Row>
        </Card>

        <div className="d-flex gap-2">
          <Button type="submit" className="hrms-btn-primary" disabled={busy}>
            <FiSave className="me-1" /> {busy ? 'Saving…' : (isEdit ? 'Save changes' : 'Save')}
          </Button>
          {!isEdit && (
            <Button variant="outline-secondary" onClick={onSaveDraft} disabled={busy}>
              <FiFileText className="me-1" /> Save as draft
            </Button>
          )}
          <Button variant="light" onClick={() => navigate(isEdit ? `/retailers/${cmGuid}` : '/retailers')} disabled={busy}>Cancel</Button>
        </div>
      </Form>

      <ConfirmDialog
        show={gstPrompt}
        title="GSTIN is blank"
        message="No GSTIN entered. Do you want to fill it before saving?"
        confirmText="Save without GSTIN"
        cancelText="Go back & fill"
        variant="primary"
        onCancel={() => setGstPrompt(false)}
        onConfirm={() => { setGstPrompt(false); commit(); }}
      />
    </div>
  );
}
